/**
 * Mi Gestión de Cobros — widget personal del Inicio.
 *
 * El Inicio lo monta solo para usuarios con el programa "gestioncobros" y se
 * muestra siempre que haya permiso, con estado vacío si el usuario aún no
 * tiene cartera ni promesas (solo se oculta mientras carga o si el endpoint
 * falla).
 *
 * Muestra la cola de promesas de SU cartera (vencidas primero, igual que la
 * pantalla de Gestión de Cobros); clic en una promesa abre el expediente del
 * préstamo. Datos de /api/gestioncobros_midia.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import { useApi } from "lib/api";
import { FP } from "components/financepro/tokens";

const MAX_PROMESAS = 5;

const money = (v) =>
  Number(v || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** dd/mm/aaaa de hoy, mismo formato en que el backend manda fechapromesa. */
const hoyCorta = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

function Stat({ valor, etiqueta, alerta }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 800, fontSize: "1.25rem", color: alerta ? "#C62828" : FP.color.textPrimary }}>
        {valor}
      </Typography>
      <Typography sx={{ fontSize: "0.74rem", color: FP.color.textSecondary }}>{etiqueta}</Typography>
    </Box>
  );
}

function MiGestionCobros() {
  const navigate = useNavigate();
  const { post } = useApi();
  const [datos, setDatos] = useState(null);

  useEffect(() => {
    let activo = true;
    post("/api/gestioncobros_midia").then(({ ok, data }) => {
      if (activo && ok) setDatos(data);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const promesas = datos?.promesas || [];
  if (!datos) return null;

  const vencidas = promesas.filter((p) => p.estadopromesa === "vencida").length;
  const hoy = hoyCorta();
  const visibles = promesas.slice(0, MAX_PROMESAS);

  const chipDe = (p) => {
    if (p.estadopromesa === "vencida") return { label: `Vencida · ${p.fechapromesa}`, color: "error" };
    if (p.fechapromesa === hoy) return { label: "Hoy", color: "warning" };
    return { label: p.fechapromesa || "Sin fecha", color: "default" };
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", letterSpacing: "0.08em", color: FP.color.textSecondary, mb: 1.2 }}>
        MI GESTIÓN DE COBROS
      </Typography>
      <Card elevation={0} sx={{ borderRadius: 2.5, border: "1px solid #E5E9F0", boxShadow: FP.shadow.soft }}>
        <Box sx={{ display: "flex", gap: { xs: 3, sm: 5 }, flexWrap: "wrap", p: 2, pb: 1.4 }}>
          <Stat valor={datos.cartera} etiqueta="Préstamos asignados" />
          <Stat valor={promesas.length} etiqueta="Promesas abiertas" />
          <Stat valor={vencidas} etiqueta="Promesas vencidas" alerta={vencidas > 0} />
          <Stat valor={datos.gestioneshoy} etiqueta="Gestiones hoy" />
        </Box>

        {visibles.length === 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.6, borderTop: "1px solid #EEF1F6" }}>
            <Icon sx={{ fontSize: 20, color: FP.color.textSecondary }}>
              {datos.cartera > 0 ? "task_alt" : "assignment_ind"}
            </Icon>
            <Typography sx={{ fontSize: "0.84rem", color: FP.color.textSecondary }}>
              {datos.cartera > 0
                ? "Sin promesas abiertas en tu cartera."
                : "Aún no tienes préstamos asignados como gestor; se asignan desde la pestaña Gestión del expediente."}
            </Typography>
          </Box>
        )}

        {visibles.length > 0 && (
          <Box sx={{ px: 1, pb: 1 }}>
            {visibles.map((p) => (
              <Box
                key={p.id}
                onClick={() => navigate(`/prestamos/expediente/${p.noprest}`)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.4,
                  px: 1,
                  py: 1.1,
                  borderTop: "1px solid #EEF1F6",
                  cursor: "pointer",
                  borderRadius: 1,
                  "&:hover": { bgcolor: `${FP.color.navy}08` },
                }}
              >
                <Icon sx={{ fontSize: 20, color: FP.color.navy, flexShrink: 0 }}>event</Icon>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontWeight: 700, fontSize: "0.88rem", color: FP.color.textPrimary }}>
                    {String(p.cliente || "").trim() || `Préstamo #${p.noprest}`}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: "0.74rem", color: FP.color.textSecondary }}>
                    Préstamo #{p.noprest}
                    {p.celular || p.telefono ? ` · ${p.celular || p.telefono}` : ""}
                  </Typography>
                </Box>
                <Chip size="small" variant="outlined" {...chipDe(p)} sx={{ fontSize: "0.7rem" }} />
                {p.montopromesa > 0 && (
                  <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: FP.color.textPrimary, flexShrink: 0 }}>
                    ${money(p.montopromesa)}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        <Box
          onClick={() => navigate("/prestamos/gestioncobros")}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.6,
            py: 1.1,
            borderTop: "1px solid #EEF1F6",
            cursor: "pointer",
            color: FP.color.navy,
            "&:hover": { bgcolor: `${FP.color.navy}08` },
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
            {promesas.length > MAX_PROMESAS
              ? `Ver las ${promesas.length} promesas en Gestión de Cobros`
              : "Ver Gestión de Cobros"}
          </Typography>
          <Icon sx={{ fontSize: 16 }}>arrow_forward</Icon>
        </Box>
      </Card>
    </Box>
  );
}

export default MiGestionCobros;
